import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateInvestorProcessSettings } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';

interface Props {
	activeInvestorId: string | undefined;
	kycWorkflowId?: string;
}

export const useUpdateInvestorProcessSettings = ({ activeInvestorId, kycWorkflowId }: Props) => {
	const queryClient = useQueryClient();
	const updateInvestorProcessSettingsRequest = useMutation({
		mutationFn: updateInvestorProcessSettings,
	});
	const tMipManage = useTranslationMipManage();

	const handleRequestMorePivDocs = (areMorePivDocsRequested: boolean, comment?: string) => {
		if (!activeInvestorId) {
			throw new Error('Active investor not found');
		}

		updateInvestorProcessSettingsRequest.mutate(
			{
				investorId: activeInvestorId,
				body: {
					areMorePivDocsRequested,
					morePivDocsRequestedComment: comment,
				},
				kycWorkflowId,
			},
			{
				onSuccess: async () => {
					// once the KYC status is overridden, refresh KYC data
					await queryClient.invalidateQueries({
						queryKey: [API_PATHS.MIP_KYC_INVESTORS_GET],
					});

					await queryClient.invalidateQueries({
						queryKey: [API_PATHS.MIP_KYC_INVESTORS_INVESTORID_GET],
					});
					showToastV2({
						title: tMipManage('kyc.review.piv_docs_requested'),
						message: tMipManage('kyc.review.piv_docs_requested_message'),
						state: 'success',
					});
				},
			}
		);
	};

	return { handleRequestMorePivDocs, updateInvestorProcessSettingsRequest };
};
